import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  FlaskConical,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Loader2,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LabTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const categories = ['blood', 'urine', 'hormones', 'cardiac', 'imaging', 'pathology', 'genetics', 'allergy', 'qualitative'];

  const [newTemplate, setNewTemplate] = useState({
    test_name: '',
    category: 'blood',
    result_type: 'quantitative',
    parameters: [{ name: '', unit: '', reference_range: '' }]
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/lab-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.test_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || !categoryFilter || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const addParameter = (isNew = false) => {
    if (isNew) {
      setNewTemplate(prev => ({
        ...prev,
        parameters: [...prev.parameters, { name: '', unit: '', reference_range: '' }]
      }));
    } else if (editingTemplate) {
      setEditingTemplate(prev => ({
        ...prev,
        parameters: [...prev.parameters, { name: '', unit: '', reference_range: '' }]
      }));
    }
  };

  const removeParameter = (index, isNew = false) => {
    if (isNew) {
      setNewTemplate(prev => ({
        ...prev,
        parameters: prev.parameters.filter((_, i) => i !== index)
      }));
    } else if (editingTemplate) {
      setEditingTemplate(prev => ({
        ...prev,
        parameters: prev.parameters.filter((_, i) => i !== index)
      }));
    }
  };

  const updateParameter = (index, field, value, isNew = false) => {
    if (isNew) {
      setNewTemplate(prev => {
        const params = [...prev.parameters];
        params[index] = { ...params[index], [field]: value };
        return { ...prev, parameters: params };
      });
    } else if (editingTemplate) {
      setEditingTemplate(prev => {
        const params = [...prev.parameters];
        params[index] = { ...params[index], [field]: value };
        return { ...prev, parameters: params };
      });
    }
  };

  const handleSaveNew = async () => {
    if (!newTemplate.test_name || newTemplate.parameters.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/lab-templates`, newTemplate);
      setTemplates(prev => [...prev, response.data]);
      setIsCreating(false);
      setNewTemplate({
        test_name: '',
        category: 'blood',
        result_type: 'quantitative',
        parameters: [{ name: '', unit: '', reference_range: '' }]
      });
      toast.success('Template created successfully');
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingTemplate) return;

    setSaving(true);
    try {
      const response = await axios.put(`${API}/lab-templates/${editingTemplate.id}`, editingTemplate);
      setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? response.data : t));
      setEditingTemplate(null);
      toast.success('Template updated successfully');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error(error.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API}/lab-templates/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const renderParameterForm = (parameters, isNew) => (
    <div className="space-y-3">
      <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
        <div className="col-span-4">Parameter Name</div>
        <div className="col-span-3">Unit</div>
        <div className="col-span-5">Reference Range</div>
      </div>
      {parameters.map((param, index) => (
        <div key={`param-${index}`} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-4">
            <Input
              value={param.name}
              onChange={(e) => updateParameter(index, 'name', e.target.value, isNew)}
              placeholder="e.g., Hemoglobin"
              className="h-8 text-sm"
              data-testid={`param-name-${index}`}
            />
          </div>
          <div className="col-span-3">
            <Input
              value={param.unit}
              onChange={(e) => updateParameter(index, 'unit', e.target.value, isNew)}
              placeholder="e.g., g/dL"
              className="h-8 text-sm"
              data-testid={`param-unit-${index}`}
            />
          </div>
          <div className="col-span-4">
            <Input
              value={param.reference_range}
              onChange={(e) => updateParameter(index, 'reference_range', e.target.value, isNew)}
              placeholder="e.g., 12-17 g/dL"
              className="h-8 text-sm"
              data-testid={`param-ref-${index}`}
            />
          </div>
          <div className="col-span-1 flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-700"
              onClick={() => removeParameter(index, isNew)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => addParameter(isNew)}
        className="mt-2"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Parameter
      </Button>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="lab-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Lab Test Templates</h1>
            <p className="text-muted-foreground mt-1">Manage test templates and parameters</p>
          </div>
          <Button onClick={() => setIsCreating(true)} className="bg-sky-600 hover:bg-sky-700">
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Create New Template Form */}
        {isCreating && (
          <div className="card-clinical border-2 border-sky-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Create New Template</h2>
              <Button variant="ghost" size="icon" onClick={() => setIsCreating(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Test Name *</Label>
                  <Input
                    value={newTemplate.test_name}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, test_name: e.target.value }))}
                    placeholder="e.g., Complete Blood Count"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={newTemplate.category} onValueChange={(v) => setNewTemplate(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Result Type</Label>
                  <Select value={newTemplate.result_type} onValueChange={(v) => setNewTemplate(prev => ({ ...prev, result_type: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quantitative">Quantitative (Numeric)</SelectItem>
                      <SelectItem value="positive_negative">Positive / Negative</SelectItem>
                      <SelectItem value="categorical">Categorical (Options)</SelectItem>
                      <SelectItem value="culture_sensitivity">Culture & Sensitivity (M/C/S)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="mb-2 block">Parameters</Label>
                                {renderParameterForm(newTemplate.parameters, true)}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreating(false)}>Cancel</Button>
                <Button onClick={handleSaveNew} disabled={saving} className="bg-sky-600 hover:bg-sky-700">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Create Template
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Templates List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTemplates.map(template => (
              <div key={template.id} className="card-clinical">
                {/* Template Header */}
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                >
                  <div className="flex items-center gap-3">
                    <FlaskConical className="w-5 h-5 text-sky-600" />
                    <div>
                      <h3 className="font-semibold text-foreground">{template.test_name}</h3>
                      <p className="text-sm text-muted-foreground">{template.parameters?.length || 0} parameters</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{template.category}</Badge>
                    {expandedTemplate === template.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedTemplate === template.id && (
                  <div className="mt-4 pt-4 border-t">
                    {editingTemplate?.id === template.id ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Test Name</Label>
                            <Input
                              value={editingTemplate.test_name}
                              onChange={(e) => setEditingTemplate(prev => ({ ...prev, test_name: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Category</Label>
                            <Select value={editingTemplate.category} onValueChange={(v) => setEditingTemplate(prev => ({ ...prev, category: v }))}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="mb-2 block">Parameters</Label>
                                                    {renderParameterForm(editingTemplate.parameters, false)}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                          <Button onClick={handleUpdate} disabled={saving} className="bg-sky-600 hover:bg-sky-700">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Parameters Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 text-muted-foreground font-medium">Parameter</th>
                                <th className="text-left py-2 text-muted-foreground font-medium">Unit</th>
                                <th className="text-left py-2 text-muted-foreground font-medium">Reference Range</th>
                              </tr>
                            </thead>
                            <tbody>
                              {template.parameters?.map((param, idx) => (
                                <tr key={idx} className="border-b last:border-0">
                                  <td className="py-2 font-medium">{param.name}</td>
                                  <td className="py-2 text-muted-foreground">{param.unit}</td>
                                  <td className="py-2 text-muted-foreground">{param.reference_range}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2 mt-4">
                          <Button variant="outline" size="sm" onClick={() => setEditingTemplate(template)}>
                            <Pencil className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(template.id)}>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}

            {filteredTemplates.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No templates found. Create your first template to get started.
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LabTemplatesPage;
